import { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { imgUrl, logoUrl, runJob } from "@/lib/glossyApi";
import {
  RefreshCw, Save, Sparkles, PenLine, AlertTriangle, Send, Image as ImageIcon, X, Calendar, MapPin, Images, Upload,
} from "lucide-react";
import { AD_SLOT_META } from "./shared";
import SavedImagePicker from "./SavedImagePicker";

const PLACEMENTS = [
  { id: "homepage", label: "Homepage — picture-first, cinematic", aspect: "aspect-[3/4]", note: "Big cinematic photo dominates, minimal overlay text. The 'big ones'." },
  { id: "edit_inline", label: "The Edit inline — 1:1 picture-first", aspect: "aspect-square", note: "Same cinematic quality as homepage, square-cropped." },
];

export default function AdvertSection({ member, setMember, onChange }) {
  const m = member;
  const ad = (m.package && m.package.advert) || {};
  const run = m.advert_run || {};
  const [placement, setPlacement] = useState(ad.placement === "banner" ? "homepage" : (ad.placement || "homepage"));
  const [headline, setHeadline] = useState(ad.headline || "");
  const [copy, setCopy] = useState(ad.copy || "");
  const [linkUrl, setLinkUrl] = useState(ad.link_url || m.website || "");
  const [accent, setAccent] = useState(ad.accent || "#141414");
  const [runSlot, setRunSlot] = useState(run.slot || "home_band_a");
  const [runIssue, setRunIssue] = useState(run.issue || "");
  const [editions, setEditions] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [adStyle, setAdStyle] = useState("");
  const photoCount = (m.photos || []).length;
  const track = (fn) => (e) => { fn(e.target.value); setDirty(true); };
  const cur = PLACEMENTS.find((p) => p.id === placement) || PLACEMENTS[0];

  useEffect(() => {
    client.get("/admin/editions/upcoming").then((r) => setEditions(r.data || [])).catch(() => setEditions([]));
  }, []);

  useEffect(() => {
    client.get("/admin/ad-spaces", { params: runIssue ? { issue: runIssue } : {} })
      .then((r) => setSpaces(r.data.spaces || [])).catch(() => setSpaces([]));
  }, [runIssue, run.status, run.slot]); // eslint-disable-line react-hooks/exhaustive-deps

  const swapAdImage = async (path) => {
    setBusy("swap");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image: path, target: "advert_image" });
      setMember({ ...m, ...(r.data.member || {}) });
      setShowPicker(false);
      toast.success("Advert image swapped — no credits spent.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't swap."); }
    setBusy("");
  };

  const uploadAdImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload-ad");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await client.post(`/admin/members/${m.id}/cover-upload?target=advert_image`, fd);
      toast.success("Your photo is the advert image now — no AI, no credits.");
      onChange && onChange();
    } catch { toast.error("Upload failed — try again."); }
    setBusy(""); e.target.value = "";
  };

  const chosenSpace = spaces.find((s) => s.id === runSlot) || { shape: (AD_SLOT_META[runSlot] || {}).shape || "band" };

  const draftWithAI = async () => {
    if (!window.confirm("Draft advert copy using the Master section? Spends AI credits.")) return;
    setBusy("draft");
    try {
      const r = await client.post(`/admin/members/${m.id}/generate-advert`);
      const nx = r.data || {};
      setHeadline(nx.headline || ""); setCopy(nx.copy || ""); setLinkUrl(nx.link_url || linkUrl);
      setMember({ ...m, package: { ...(m.package || {}), advert: { ...ad, ...nx } } });
      setDirty(false);
      toast.success("Advert copy drafted.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed."); }
    setBusy("");
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await client.patch(`/admin/members/${m.id}/advert-settings`, { headline, copy, link_url: linkUrl, placement, accent });
      setMember({ ...m, package: { ...(m.package || {}), advert: r.data } });
      setDirty(false);
      toast.success("Advert saved.");
    } catch { toast.error("Save failed."); }
    setBusy("");
  };

  const generateImage = async () => {
    if (photoCount < 3) { toast.error("Add at least 3 Master Photos first."); return; }
    if (!window.confirm("Generate a fresh advert image? Spends AI credits.")) return;
    setBusy("image");
    try {
      await client.patch(`/admin/members/${m.id}/advert-settings`, { placement });
      const r = await runJob(`/admin/members/${m.id}/generate-advert-image`, { style: adStyle });
      setMember({ ...m, package: { ...(m.package || {}), advert: { ...ad, image: r.data.image, placement } } });
      toast.success(`${cur.label.split(" — ")[0]} advert image generated.`);
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Image generation failed."); }
    setBusy("");
  };

  const runAction = async (action) => {
    const needsImage = (chosenSpace.shape || "band") !== "banner";
    if (action !== "unpublish" && (!headline || (needsImage && !ad.image))) {
      toast.error(needsImage ? "The advert needs an image and a headline first." : "The advert needs a headline first — banner spaces don't need a photo.");
      return;
    }
    if (action === "schedule" && !runIssue) { toast.error("Pick an edition first."); return; }
    const label = (AD_SLOT_META[runSlot] || {}).label || runSlot;
    const heldBy = (spaces.find((s) => s.id === runSlot) || {}).holder;
    const swapping = action !== "unpublish" && heldBy && heldBy.member_id !== m.id;
    const msg = action === "publish_now" ? (swapping ? `"${label}" is held by ${heldBy.business_name}. SWAP them out and run this advert LIVE in their place?` : `Run this advert LIVE now in "${label}"?`)
      : action === "schedule" ? (swapping ? `"${label}" is held by ${heldBy.business_name}. SWAP them out for the ${prettyDate(runIssue)} edition?` : `Schedule this advert into "${label}" for the ${prettyDate(runIssue)} edition?`)
      : "Take this advert off the magazine?";
    if (!window.confirm(msg)) return;
    setBusy(action);
    try {
      if (dirty) await client.patch(`/admin/members/${m.id}/advert-settings`, { headline, copy, link_url: linkUrl, placement, accent });
      const payload = { action, slot: runSlot };
      if (swapping) payload.replace = true;
      if (action === "schedule") payload.issue = runIssue;
      const r = await client.post(`/admin/members/${m.id}/advert/run`, payload);
      setMember({ ...m, advert_run: r.data.advert_run });
      setDirty(false);
      toast.success(action === "publish_now" ? "Advert is LIVE." : action === "schedule" ? "Advert scheduled." : "Advert taken off.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    setBusy("");
  };

  const isLive = run.status === "live";
  const isScheduled = run.status === "scheduled";
  const slotLabel = run.slot ? ((AD_SLOT_META[run.slot] || {}).label || run.slot) : null;

  return (
    <section id="section-advert" data-testid="advert-section" className="space-y-4 pt-4 scroll-mt-24">
      <div className="bg-ink text-cream px-5 py-3 grain flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="overline text-gloss-gold">Advert · paid placement in the magazine</span>
        <span className={`ml-auto font-mono text-[10px] px-2 py-0.5 ${isLive ? "bg-green-600 text-white" : isScheduled ? "bg-gloss-gold text-ink" : "bg-cream/10 text-cream/50"}`} data-testid="advert-run-pill">
          {isLive ? "LIVE" : isScheduled ? "SCHEDULED" : "NOT RUNNING"}
        </span>
      </div>

      {photoCount < 3 && (
        <div className="border border-gloss-gold/50 bg-gloss-gold/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-gloss-gold shrink-0 mt-0.5" />
          <p className="font-sans text-sm text-ink"><b>Image generation locked.</b> Add at least 3 Master Photos first.</p>
        </div>
      )}

      <div className="border border-ink/15 bg-white" data-testid="advert-card">
        {/* 1 · Look */}
        <div className="p-5 sm:p-6 border-b border-ink/10">
          <p className="overline text-ink/60 mb-3">1 · The visual advert look (drives the image shape — banners don&apos;t need this)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLACEMENTS.map((p) => (
              <button key={p.id} onClick={() => { setPlacement(p.id); setDirty(true); }} data-testid={`advert-placement-${p.id}`}
                className={`text-left border p-3 transition-colors ${placement === p.id ? "border-gloss-gold bg-gloss-gold/10" : "border-ink/20 hover:border-ink/40"}`}>
                <p className="overline text-[10px] text-ink">{p.label.split(" — ")[0]}</p>
                <p className="font-sans text-xs text-ink/55 mt-1">{p.note}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 2 · The two creatives */}
        <div className="p-5 sm:p-6 border-b border-ink/10">
          <p className="overline text-ink/60 mb-3">2 · The two creatives — the right one runs automatically in the space you pick</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div data-testid="advert-visual-creative">
              <div className="flex items-baseline justify-between mb-2">
                <p className="overline text-[10px] text-ink">Visual advert — photo spaces</p>
                <span className="font-mono text-[9px] px-2 py-0.5 bg-ink text-cream">CREDITS</span>
              </div>
              <div className={`relative ${cur.aspect} bg-ink border border-ink/15 overflow-hidden`} data-testid="advert-preview">
                {ad.image ? (
                  <img src={imgUrl({ cover_image: ad.image })} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-cream/40">
                    <ImageIcon size={26} /> <p className="overline text-[10px]">no advert image yet</p>
                  </div>
                )}
                {busy === "image" && (
                  <div className="absolute inset-0 bg-ink/80 flex items-center justify-center text-cream">
                    <RefreshCw size={20} className="animate-spin" />
                  </div>
                )}
              </div>
              <div className="mt-3">
                <label className="overline text-[10px] text-ink/60 block mb-1">Art direction — your words drive the shot</label>
                <textarea value={adStyle} onChange={(e) => setAdStyle(e.target.value)} rows={2}
                  placeholder="e.g. real documentary campaign shot of their actual product on site, natural light, gritty and honest — no glossy AI look"
                  className="w-full border border-ink/30 px-3 py-2 font-sans text-xs focus:outline-none focus:border-ink" data-testid="advert-style-prompt" />
              </div>
              <button onClick={generateImage} disabled={photoCount < 3 || !!busy}
                className="mt-3 w-full bg-gloss-gold text-ink px-4 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="advert-generate-image-btn">
                {busy === "image" ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {ad.image ? "Regenerate (credits)" : `Generate ${cur.label.split(" — ")[0].toLowerCase()} image`}
              </button>
              <button onClick={() => setShowPicker(!showPicker)} disabled={!!busy}
                className="mt-2 w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="advert-swap-btn">
                <Images size={14} /> {showPicker ? "Close picker" : "Swap from saved (free)"}
              </button>
              <label className="mt-2 w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 cursor-pointer hover:bg-ink hover:text-cream transition-colors" data-testid="advert-upload-btn">
                <Upload size={14} /> {busy === "upload-ad" ? "Uploading…" : "Upload your own (free)"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadAdImage} data-testid="advert-upload-input" />
              </label>
              {showPicker && (
                <SavedImagePicker member={m} current={ad.image} tid="advert-swap" busy={busy}
                  note="Pick any saved image as the advert visual — free, no credits."
                  onPick={swapAdImage} />
              )}
              <p className="font-mono text-[10px] text-ink/45 mt-2">Runs in the bands, squares and billboards. Anchored on Master Photos — same cinematic quality as the big ones.</p>
            </div>
            <div data-testid="advert-banner-creative">
              <div className="flex items-baseline justify-between mb-2">
                <p className="overline text-[10px] text-ink">Banner strip — slim spaces</p>
                <span className="font-mono text-[9px] px-2 py-0.5 bg-green-700 text-white">FREE</span>
              </div>
              <div className="relative flex items-center gap-4 border border-ink/15 text-cream px-4 sm:px-6 py-5 grain overflow-hidden" style={{ backgroundColor: accent || ad.accent || "#141414" }} data-testid="advert-banner-preview">
                {logoUrl(m) && <img src={logoUrl(m)} alt="" className="h-8 w-auto object-contain shrink-0 relative z-10" />}
                <div className="min-w-0 flex-1 relative z-10">
                  <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-cream/50">Advertisement</p>
                  <p className="font-serif text-base sm:text-xl leading-tight tracking-tight truncate">{headline || "Your headline appears here"}</p>
                  <p className="font-sans text-[11px] text-cream/70 truncate">{m.business_name}{m.borough ? ` · ${m.borough}` : ""}</p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1.5 border border-cream/60 px-3 py-1.5 overline text-[9px] shrink-0 relative z-10">Discover</span>
              </div>
              <p className="font-mono text-[10px] text-ink/45 mt-2">Built live from your headline, logo and brand colour — always current, never spends a credit. Every slim banner space runs this automatically; no photo needed.</p>
            </div>
          </div>
        </div>

        {/* 3 · The words */}
        <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start border-b border-ink/10">
          <div className="space-y-3">
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Headline (feeds both creatives)</label>
              <input value={headline} onChange={track(setHeadline)} placeholder="A punchy ad headline"
                className="w-full border border-ink/30 px-4 py-2.5 font-serif text-lg focus:outline-none focus:border-ink" data-testid="advert-headline" />
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Copy (one line)</label>
              <textarea value={copy} onChange={track(setCopy)} rows={3} placeholder="The one-line pitch"
                className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="advert-copy" />
            </div>
          </div>
          <div className="space-y-3">
            <button onClick={draftWithAI} disabled={!!busy}
              className="w-full bg-ink text-cream px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="advert-draft-btn">
              {busy === "draft" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} Draft with AI
            </button>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Click-through URL</label>
              <input type="url" value={linkUrl} onChange={track(setLinkUrl)} placeholder="https://…"
                className="w-full border border-ink/30 px-4 py-2.5 font-mono text-[12px] focus:outline-none focus:border-ink" data-testid="advert-link-url" />
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Brand colour — banner &amp; square background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(accent) ? accent : "#141414"} onChange={(e) => { setAccent(e.target.value); setDirty(true); }}
                  className="w-11 h-10 border border-ink/30 cursor-pointer p-0.5 bg-transparent" data-testid="advert-accent-picker" />
                <input value={accent} onChange={track(setAccent)} placeholder="#141414"
                  className="flex-1 border border-ink/30 px-3 py-2.5 font-mono text-[12px] focus:outline-none focus:border-ink" data-testid="advert-accent-hex" />
              </div>
              <p className="font-sans text-[11px] text-ink/45 mt-1">Paste the member's exact brand hex, or pick one. Save copy to apply.</p>
            </div>
          </div>
        </div>

        {/* 4 · Where it runs */}
        <div className="p-5 sm:p-6 border-b border-ink/10" data-testid="advert-run-panel">
          <p className="overline text-ink/60 mb-1 flex items-center gap-1.5"><MapPin size={12} /> 4 · Where it runs in the magazine</p>
          {(isLive || isScheduled) ? (
            <p className="font-sans text-sm text-ink mb-3" data-testid="advert-run-current">
              {isLive ? "Running LIVE" : "Scheduled"} in <b>{slotLabel}</b>{run.issue ? <> · <b>{prettyDate(run.issue)}</b> edition</> : null}.
              <span className="text-ink/50"> {(AD_SLOT_META[run.slot] || {}).where}</span>
            </p>
          ) : (
            <p className="font-sans text-sm text-ink/55 mb-3">Not on the magazine yet. Pick a space and an edition, then run it live or schedule it.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Advert space — availability for the chosen edition</label>
              <select value={runSlot} onChange={(e) => setRunSlot(e.target.value)}
                className="w-full border border-ink/30 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink bg-white" data-testid="advert-run-slot">
                {(spaces.length ? spaces : Object.entries(AD_SLOT_META).map(([id, s]) => ({ id, ...s }))).map((s) => {
                  const takenByOther = s.holder && s.holder.member_id !== m.id;
                  const mine = s.holder && s.holder.member_id === m.id;
                  const carry = s.holder && s.holder.carry_over;
                  const fam = s.shape === "banner" ? "BANNER" : "VISUAL";
                  return (
                    <option key={s.id} value={s.id}>
                      {s.label} · {fam} — {mine ? "YOURS" : carry ? `HELD · ${s.holder.business_name} (carries over — select to swap)` : takenByOther ? `TAKEN · ${s.holder.business_name} (select to swap them out)` : "FREE"}
                    </option>
                  );
                })}
              </select>
              <p className="font-sans text-xs text-ink/45 mt-1">{(AD_SLOT_META[runSlot] || {}).where}</p>
              <p className={`font-mono text-[10px] mt-1 ${chosenSpace.shape === "banner" ? "text-gloss-gold" : "text-ink/50"}`} data-testid="advert-shape-note">
                {chosenSpace.shape === "banner"
                  ? "BANNER space — runs the FREE Banner strip creative above. Your photo is not used and no credits are ever spent."
                  : "VISUAL space — runs the cinematic Visual advert creative above."}
              </p>
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1 flex items-center gap-1"><Calendar size={11} /> Edition (for scheduling)</label>
              <select value={runIssue} onChange={(e) => setRunIssue(e.target.value)}
                className="w-full border border-ink/30 px-3 py-2.5 font-mono text-[12px] focus:outline-none focus:border-ink bg-white" data-testid="advert-run-issue">
                <option value="">— pick an edition —</option>
                {editions.map((ed) => <option key={ed.starts} value={ed.starts}>{prettyDate(ed.starts)} · Issue #{ed.issue_no}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => runAction("publish_now")} disabled={!!busy}
              className="bg-gloss-gold text-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="advert-run-now-btn">
              {busy === "publish_now" ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />} Run live now
            </button>
            <button onClick={() => runAction("schedule")} disabled={!!busy || !runIssue}
              className="border border-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="advert-schedule-btn">
              {busy === "schedule" ? <RefreshCw size={13} className="animate-spin" /> : <Calendar size={13} />} Schedule
            </button>
            {(isLive || isScheduled) && (
              <button onClick={() => runAction("unpublish")} disabled={!!busy}
                className="border border-ink/30 text-ink/70 px-4 py-2.5 overline flex items-center gap-2 hover:border-red-600 hover:text-red-600 transition-colors disabled:opacity-40" data-testid="advert-take-off-btn">
                {busy === "unpublish" ? <RefreshCw size={13} className="animate-spin" /> : <X size={13} />} Take off
              </button>
            )}
          </div>
          <p className="font-mono text-[10px] text-ink/40 mt-2">One member per space per edition — if a space is taken you&apos;ll be told who holds it.</p>
        </div>

        {/* Footer: save */}
        <div className="p-5 sm:p-6 flex items-center justify-between gap-3 flex-wrap">
          <p className={`font-mono text-[11px] ${dirty ? "text-gloss-gold" : isLive ? "text-green-700" : "text-ink/40"}`} data-testid="advert-status">
            {dirty ? "Unsaved changes" : isLive ? `· LIVE IN ${(slotLabel || "").toUpperCase()} ·` : isScheduled ? `· SCHEDULED · ${prettyDate(run.issue)}` : "Draft"}
          </p>
          <button onClick={save} disabled={!!busy || !dirty}
            className="border border-ink px-5 py-2.5 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="advert-save-btn">
            {busy === "save" ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </section>
  );
}

function prettyDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}
